import { Button, Checkbox, Group, Modal, Text, Title } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import * as React from "react";
import * as XLSX from "xlsx";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import type { ReturnSerialRow } from "../types";

/** Một dòng serial đọc từ tệp Excel: cột Serial, SerialStatus, SerialNote như file mẫu */
export interface ExcelSerialRow {
  serial: string;
  serialStatus: string;
  serialNote: string;
}

const TEMPLATE_NOTE =
  "Ghi chú: Vui lòng nhập đầy đủ thông tin vào đúng hàng và đúng cột. Đối với các mã, vui lòng để định dạng là Text.";

/** Tải file mẫu import serial (cùng cột với file mẫu của trang nhập kho) */
export const downloadSerialTemplate = () => {
  const headers = ["Serial", "SerialStatus", "SerialNote", TEMPLATE_NOTE];
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ["MSLOGM19674631", "Bán bình thường", "Ghi chú tình trạng"]]);
  worksheet["!cols"] = headers.map((header) => ({ wch: header.length + 3 }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Danh sách");
  XLSX.writeFile(workbook, "Mau Import Excel Serial.xlsx");
};

/** Mở hộp chọn tệp Excel; đọc xong thì trả workbook */
export const pickExcelWorkbook = (onLoaded: (workbook: XLSX.WorkBook) => void) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xlsx, .xls";
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) {
      NotificationExtension.Warn("Không có tập tin nào được chọn");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        onLoaded(XLSX.read(event.target?.result, { type: "array" }));
      } catch (error) {
        console.error(error);
        NotificationExtension.Warn("Không đọc được tệp Excel");
      }
    };
    reader.readAsArrayBuffer(file);
  };
  input.click();
};

/** Đọc các dòng có Serial của sheet đã chọn; serial trùng chỉ lấy dòng đầu */
export const readSerialRowsFromSheet = (workbook: XLSX.WorkBook, sheetName: string): ExcelSerialRow[] => {
  const json: Record<string, unknown>[] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: false });
  const seen = new Set<string>();
  const rows: ExcelSerialRow[] = [];
  json.forEach((item) => {
    const serial = String(item.Serial ?? "").trim();
    if (!serial || seen.has(serial)) return;
    seen.add(serial);
    rows.push({
      serial,
      serialStatus: String(item.SerialStatus ?? "").trim(),
      serialNote: String(item.SerialNote ?? "").trim(),
    });
  });
  return rows;
};

const formatMoney = (value: unknown) => (Number(value) ? Number(value).toLocaleString() : "");

/** Xuất danh sách serial trên phiếu ra Excel, cột theo lưới serial */
export const exportReturnSerialsToExcel = (rows: ReturnSerialRow[]) => {
  if (!rows.length) {
    NotificationExtension.Fails("Không có dữ liệu để xuất!");
    return;
  }
  const header = [
    "Mã vị trí",
    "Vị trí kho",
    "Mã hàng",
    "Serial",
    "Tên SP",
    "ĐVT1",
    "SL1",
    "Giá nhập",
    "Lý do đổi trả",
    "Thành tiền",
    "Tình trạng",
    "Ghi chú tình trạng",
    "Ghi chú",
  ];
  const data = rows.map((row) => [
    row.positionCode ?? "",
    row.positionName ?? "",
    row.itemCode ?? "",
    row.serial ?? "",
    row.itemName ?? "",
    row.primaryUomName || row.primaryUomCode || "",
    row.primaryQuantity ?? "",
    formatMoney(row.poPrice),
    row.returnReason ?? "",
    formatMoney((Number(row.poPrice) || 0) * (Number(row.primaryQuantity) || 0)),
    row.serialStatus ?? "",
    row.attribute11 ?? "",
    row.note ?? "",
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...data]), "Phiếu nhập kho");
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}-${pad(
    now.getMinutes(),
  )}-${pad(now.getSeconds())}`;
  XLSX.writeFile(workbook, `Phieu_nhap_kho_${stamp}.xlsx`);
};

interface ExcelSheetModalProps {
  workbook: XLSX.WorkBook | null;
  onClose: () => void;
  onConfirm: (rows: ExcelSerialRow[]) => void;
}

/** Chọn một sheet trong tệp Excel để import serial */
const ExcelSheetModal = ({ workbook, onClose, onConfirm }: ExcelSheetModalProps) => {
  const [selected, setSelected] = React.useState<string | null>(null);

  React.useEffect(() => setSelected(null), [workbook]);

  return (
    <Modal opened={!!workbook} onClose={onClose} title={<Title order={4}>Chọn sheet Excel</Title>}>
      {workbook?.SheetNames.map((sheet) => (
        <Checkbox
          mt={15}
          key={sheet}
          label={<Text fw={500}>{sheet}</Text>}
          checked={selected === sheet}
          onChange={(e) => setSelected(e.currentTarget.checked ? sheet : null)}
        />
      ))}
      <Group mt={15} justify="flex-end">
        <Button
          leftSection={<IconCheck size={16} />}
          disabled={!selected}
          onClick={() => {
            if (!workbook || !selected) return;
            onConfirm(readSerialRowsFromSheet(workbook, selected));
            onClose();
          }}
        >
          Xác nhận
        </Button>
      </Group>
    </Modal>
  );
};

export default ExcelSheetModal;
